import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';

import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import Loader3D from '../../../components/Loader/Loader3D';
import apiRequest from '../../../api/request';

/**
 * Targets and slots, set by a reporting head for their own team.
 *
 * Route: /Point-Tracker/target-slot-fill
 *
 * These two numbers feed four columns on the OGD Point Tracker: Revenue Target,
 * Pending, TAR and SFR. Nothing else sets them.
 *
 * WHO SEES THIS
 *
 * Only somebody with OGD people reporting to them. There is no permission flag and
 * no list to maintain, the org chart already says who runs a squad. The server
 * re-checks it on every save, so the screen deciding who to show is a convenience,
 * never the security.
 *
 * TWO KINDS OF PERSON USE THIS SCREEN
 *
 *   A REPORTING HEAD   sets their own team, between the 1st and the 5th, and once
 *                      set the target is fixed
 *   FINANCE            corrects them. Every working OGD person, any month, and an
 *                      Edit button on each row that has a target
 *
 * Which one you are is decided by the SERVER from the org chart, department 7 for
 * Finance, and arrives as meta.role. Nothing is read from localStorage: a value
 * the browser owns is a value the browser can change.
 *
 * TWO RULES, BOTH ENFORCED ON THE SERVER
 *
 *   the window     targets can only be set between the 1st and the 5th, and only
 *                  for the month that is running now
 *   set once       a target already set is fixed and cannot be changed
 *
 * The screen greys out what it can and says why, but the screen is never the rule.
 * Every save is re-checked on the server.
 *
 * ONE ROW PER PERSON PER MONTH
 *
 * Saving updates the existing row rather than adding a second. `target_slot_fill`
 * has no unique key and some people already carry two rows in one month, which the
 * Point Tracker sums and therefore double counts. Those are flagged here so
 * somebody can clean them up.
 */

const inr = (v) =>
  v === null || v === undefined || v === ''
    ? ''
    : '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function TargetSlotFill() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // which rows Finance has opened for editing. Deliberately one at a time and
  // never all at once, so a correction is a decision rather than a slip.
  const [editing, setEditing] = useState([]);
  // Finance see all 80 OGD people, so finding one by scrolling is not a plan
  const [search, setSearch] = useState('');

  const [isHead, setIsHead] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  /**
   * The month the screen opens on: the one running now.
   *
   * It has to be, because a head can only set targets for the current month and
   * only in its first five days. Opening on any other month would show a screen
   * that refuses every save.
   *
   * Finance can move this to any month, which is the whole point of their view.
   */
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const load = async (forMonth) => {
    setLoading(true);
    const res = await apiRequest({
      method: 'get',
      url: `ogd-point-tracker/my-team?month=${forMonth}`,
    });
    if (res?.status === 'success') {
      setIsHead(Boolean(res.data?.isHead) || res.data?.meta?.role === 'FINANCE');
      setRows((res.data?.rows || []).map((r) => ({ ...r })));
      setMeta(res.data?.meta || null);
      setEditing([]);
      setError('');
    } else {
      setError(res?.message || 'Could not load your team');
    }
    setLoading(false);
  };

  useEffect(() => {
    load(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const set = (userId, field, value) =>
    setRows((old) =>
      old.map((r) => (r.userId === userId ? { ...r, [field]: value } : r)),
    );

  /** Copy the first filled row down the rest. Most squads share one target. */
  const fillDown = () => {
    const first = rows.find(
      (r) => isEditable(r) && r.target !== null && r.target !== '',
    );
    if (!first) {
      enqueueSnackbar('Fill the first open row, then this copies it to everyone', {
        variant: 'info',
      });
      return;
    }
    // a locked row keeps what it already has
    setRows((old) =>
      old.map((r) =>
        isEditable(r) ? { ...r, target: first.target, slot: first.slot } : r,
      ),
    );
  };

  const save = async () => {
    // a locked row is never sent. The server would refuse the whole save, and
    // sending something that cannot change is asking for a refusal on purpose
    const toSave = rows.filter(
      (r) =>
        isEditable(r) &&
        r.target !== null &&
        r.target !== '' &&
        r.slot !== null &&
        r.slot !== '',
    );
    if (toSave.length === 0) {
      enqueueSnackbar('Nothing new to save', { variant: 'warning' });
      return;
    }

    setSaving(true);
    const res = await apiRequest({
      method: 'post',
      url: 'ogd-point-tracker/targets',
      data: {
        month,
        rows: toSave.map((r) => ({
          userId: r.userId,
          target: Number(r.target),
          slot: Number(r.slot),
        })),
      },
    });

    if (res?.status === 'success') {
      const { created = 0, updated = 0 } = res.data || {};
      enqueueSnackbar(
        isFinance
          ? `Saved. ${created} set, ${updated} corrected.`
          : `Saved. ${created} ${created === 1 ? 'target' : 'targets'} set. ` +
              'They cannot be changed now.',
        { variant: 'success' },
      );
      await load(month);
    } else {
      enqueueSnackbar(res?.message || 'Could not save', { variant: 'error' });
    }
    setSaving(false);
  };

  const pageActions = (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small"
        type="month"
        label="Month"
        InputLabelProps={{ shrink: true }}
        value={month}
        onChange={(e) => setMonth(e.target.value)}
        sx={{ width: 170 }}
      />
      <Button
        variant="outlined"
        size="small"
        onClick={() => navigate('/Point-Tracker/ogd-point-tracker')}
        startIcon={<Icon icon="mdi:table-large" />}
      >
        Point Tracker
      </Button>
    </Stack>
  );

  if (error) {
    return (
      <GlobalWrapper title="Targets and Slots" actions={pageActions} skelton={false}>
        <Alert severity="error">{error}</Alert>
      </GlobalWrapper>
    );
  }

  if (loading || saving) {
    return (
      <GlobalWrapper title="Targets and Slots" actions={pageActions} skelton={false}>
        <Loader3D />
      </GlobalWrapper>
    );
  }

  if (!isHead) {
    // two different reasons, and saying which one saves somebody asking
    const notInOgd = meta?.inOgd === false;
    return (
      <GlobalWrapper title="Targets and Slots" actions={pageActions} skelton={false}>
        <Alert severity="info" icon={<Icon icon="mdi:account-group-outline" />}>
          <Typography variant="subtitle2">
            {notInOgd ? 'This screen is for OGD only' : 'Nobody reports to you'}
          </Typography>
          <Typography variant="body2">
            {notInOgd
              ? 'Targets are set by a reporting head inside the OGD department. Your ' +
                'own department is somewhere else, so you cannot set them even if ' +
                'OGD people report to you.'
              : 'Targets are set by the person a squad reports to. If you should be ' +
                'able to set them, ask HR to check your team on the Employee Tagging ' +
                'screen.'}
          </Typography>
        </Alert>
      </GlobalWrapper>
    );
  }

  // the window, straight from the server clock rather than the browser's
  const gate = meta?.window || {};
  const isFinance = meta?.role === 'FINANCE';
  const windowOpen = Boolean(gate.open) && meta?.isCurrentMonth !== false;
  const openRows = rows.filter((r) => !r.locked);
  const canSave = windowOpen && openRows.length > 0;

  /** A row Finance has opened, or a row that was never set. */
  const isEditable = (r) =>
    isFinance ? editing.includes(r.userId) || !r.rowId : !r.locked && windowOpen;

  const toggleEdit = (userId) =>
    setEditing((old) =>
      old.includes(userId) ? old.filter((id) => id !== userId) : [...old, userId],
    );

  /**
   * Find somebody by name, employee ID, team or user ID.
   *
   * Filters what is SHOWN and nothing else. Anything typed into a row stays typed
   * whether or not the search is still matching it, and the save sends every open
   * row, not just the visible ones. Losing an entry because a filter moved would
   * be the worst possible surprise on a screen that writes targets.
   */
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? rows.filter((r) =>
        [r.name, r.empId, r.team, r.userId]
          .map((v) => String(v ?? '').toLowerCase())
          .some((v) => v.includes(needle)),
      )
    : rows;

  const filled = rows.filter((r) => r.target !== null && r.target !== '').length;
  const totalTarget = rows.reduce((a, r) => a + (Number(r.target) || 0), 0);
  const totalSlots = rows.reduce((a, r) => a + (Number(r.slot) || 0), 0);

  return (
    <GlobalWrapper title="Targets and Slots" actions={pageActions} skelton={false}>
      <Stack spacing={2}>
        <Alert severity="info" icon={<Icon icon="mdi:information-outline" />}>
          <Typography variant="subtitle2">
            {rows.length} {rows.length === 1 ? 'person' : 'people'} report to you
            {meta?.alreadySet != null && ` · ${meta.alreadySet} already set for this month`}
          </Typography>
          <Typography variant="body2">
            The <strong>revenue target</strong> feeds Revenue Target, Pending and TAR on
            the Point Tracker. The <strong>slot target</strong> feeds SFR. Fill in one
            person or everybody, then save once.
          </Typography>
        </Alert>

        {isFinance && (
          <Alert severity="info" icon={<Icon icon="mdi:bank-outline" />}>
            <Typography variant="subtitle2">Finance view</Typography>
            <Typography variant="body2">
              You are seeing <strong>every working OGD person</strong>, not one team,
              and you can correct a target that is already set. Press{' '}
              <strong>Edit</strong> on a row to open it. Every correction is stamped
              with your name and the time.
            </Typography>
          </Alert>
        )}

        {!isFinance && !windowOpen && (
          <Alert severity="warning" icon={<Icon icon="mdi:calendar-lock" />}>
            <Typography variant="subtitle2">
              {meta?.isCurrentMonth === false
                ? 'This month is closed'
                : 'Setting targets closed on the ' + (gate.lastDay || 5)}
            </Typography>
            <Typography variant="body2">
              {meta?.isCurrentMonth === false
                ? 'Targets can only be set for the month that is running now, ' +
                  gate.month + '. You are looking at ' + month + '.'
                : 'Targets can only be set between the 1st and the ' +
                  (gate.lastDay || 5) + ' of the month. Today is the ' + gate.day +
                  '. The window opens again on ' + gate.opensAgain + '.'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              A target is a promise made before the month is played, not a line drawn
              once the result is known.
            </Typography>
          </Alert>
        )}

        {!isFinance && windowOpen && (
          <Alert severity="success" icon={<Icon icon="mdi:calendar-check" />}>
            <Typography variant="subtitle2">
              Open until the {gate.lastDay || 5} of this month
            </Typography>
            <Typography variant="body2">
              Today is the {gate.day}. Once a target is saved it is fixed and cannot
              be changed, so check the numbers before saving.
            </Typography>
          </Alert>
        )}

        {meta?.duplicates > 0 && (
          <Alert severity="warning" icon={<Icon icon="mdi:alert-outline" />}>
            <Typography variant="subtitle2">
              {meta.duplicates} {meta.duplicates === 1 ? 'person has' : 'people have'} two
              rows for this month
            </Typography>
            <Typography variant="body2">
              The Point Tracker adds them together, so their target reads higher than it
              should. Saving here updates the first row and never adds another, but the
              extra rows need clearing separately.
            </Typography>
          </Alert>
        )}

        <Paper variant="outlined">
          <Box sx={{ p: 2, pb: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              flexWrap="wrap"
              useFlexGap
            >
              <Box>
                <Typography variant="subtitle2">Your team</Typography>
                <Typography variant="caption" color="text.secondary">
                  {filled} of {rows.length} filled · {inr(totalTarget)} · {totalSlots} slots
                  {openRows.length !== rows.length &&
                    ' · ' + (rows.length - openRows.length) + ' already fixed'}
                  {needle && ' · showing ' + shown.length + ' of ' + rows.length}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  placeholder="Find a person"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 230 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon icon="mdi:magnify" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch('')}>
                          <Icon icon="mdi:close" width={16} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={fillDown}
                  disabled={!isFinance && !canSave}
                  startIcon={<Icon icon="mdi:arrow-down-bold-outline" />}
                >
                  Copy first row to all
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={save}
                  disabled={saving || (!isFinance && !canSave)}
                  startIcon={<Icon icon={saving ? 'mdi:loading' : 'mdi:content-save'} />}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          </Box>
          <Divider />
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Employee ID</TableCell>
                  <TableCell sx={{ minWidth: 190 }}>Revenue Target</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Slot Target</TableCell>
                  <TableCell>Status</TableCell>
                  {/* Finance only. A head has nothing to edit, so no empty column */}
                  {isFinance && <TableCell sx={{ width: 110 }}>Edit</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {needle && shown.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isFinance ? 7 : 6}>
                      <Typography variant="body2" color="text.secondary">
                        Nobody matches that.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {shown.map((r) => (
                  <TableRow key={r.userId} hover>
                    <TableCell>
                      <Typography variant="body2">{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {r.userId}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.team}</TableCell>
                    <TableCell>
                      {r.empId ? String(r.empId).toUpperCase() : ''}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        placeholder="0"
                        // set once for a head. Finance open a row with Edit.
                        disabled={!isEditable(r)}
                        value={r.target ?? ''}
                        onChange={(e) => set(r.userId, 'target', e.target.value)}
                        InputProps={{ startAdornment: <span>₹&nbsp;</span> }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        placeholder="0"
                        disabled={!isEditable(r)}
                        value={r.slot ?? ''}
                        onChange={(e) => set(r.userId, 'slot', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      {r.hasDuplicate ? (
                        <Chip size="small" color="warning" variant="outlined" label="Two rows" />
                      ) : r.locked ? (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<Icon icon="mdi:lock-outline" width={14} />}
                          label="Fixed"
                          title="Already set for this month, and it cannot be changed"
                        />
                      ) : editing.includes(r.userId) ? (
                        <Chip size="small" color="info" variant="outlined" label="Editing" />
                      ) : r.rowId ? (
                        <Chip size="small" color="success" variant="outlined" label="Set" />
                      ) : windowOpen || isFinance ? (
                        <Chip size="small" variant="outlined" label="Not set" />
                      ) : (
                        <Chip size="small" variant="outlined" label="Closed" />
                      )}
                    </TableCell>

                    {isFinance && (
                      <TableCell>
                        {r.rowId ? (
                          <Button
                            size="small"
                            variant={editing.includes(r.userId) ? 'contained' : 'outlined'}
                            onClick={() => toggleEdit(r.userId)}
                            startIcon={
                              <Icon
                                icon={
                                  editing.includes(r.userId)
                                    ? 'mdi:lock-open-variant-outline'
                                    : 'mdi:pencil-outline'
                                }
                              />
                            }
                          >
                            {editing.includes(r.userId) ? 'Open' : 'Edit'}
                          </Button>
                        ) : (
                          // nothing set yet, so there is nothing to edit and the
                          // cell stays blank rather than saying so
                          ''
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </GlobalWrapper>
  );
}
